import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { setUserInfo } from '../lib/auth';
import { 
  formatPhoneNumber, 
  formatName,
  validatePhone, 
  validateEmail, 
  validateName, 
  validatePassword,
  getPhoneDigits 
} from '../lib/validation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SignupPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: '',
    agreeToTerms: false
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Clear field error on change
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Format phone number
    if (name === 'phone') {
      setFormData({
        ...formData,
        [name]: formatPhoneNumber(value)
      });
      return;
    }
    
    // Format names (capitalize)
    if (name === 'firstName' || name === 'lastName') {
      setFormData({
        ...formData,
        [name]: formatName(value)
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let validation;
    
    switch (name) {
      case 'email':
        validation = validateEmail(value);
        break;
      case 'phone':
        validation = validatePhone(value);
        break;
      case 'firstName':
        validation = validateName(value, 'First name');
        break;
      case 'lastName':
        validation = validateName(value, 'Last name');
        break;
      case 'password':
        validation = validatePassword(value);
        break;
      default:
        return;
    }
    
    if (validation && validation.error) {
      setFieldErrors(prev => ({ ...prev, [name]: validation.error }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // Comprehensive validation
    const errors = {};
    
    const firstNameVal = validateName(formData.firstName, 'First name');
    if (!firstNameVal.isValid) errors.firstName = firstNameVal.error || 'First name is required';
    
    const lastNameVal = validateName(formData.lastName, 'Last name');
    if (!lastNameVal.isValid) errors.lastName = lastNameVal.error || 'Last name is required';
    
    const emailVal = validateEmail(formData.email);
    if (!emailVal.isValid) errors.email = emailVal.error || 'Email is required';
    
    const phoneVal = validatePhone(formData.phone);
    if (!phoneVal.isValid) errors.phone = phoneVal.error || 'Phone number is required';
    
    const passwordVal = validatePassword(formData.password);
    if (!passwordVal.isValid) errors.password = passwordVal.error;

    // Validation
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.agreeToTerms) {
      errors.terms = 'You must agree to the terms and conditions';
    }
    
    // If any errors, show them and stop
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fix the errors below');
      return;
    }

    setLoading(true);

    try {
      // Create user account with credentials for httpOnly cookie
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/signup`, 
        {
          email: formData.email.toLowerCase().trim(),
          password: formData.password,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          phone: getPhoneDigits(formData.phone)
        },
        { withCredentials: true }
      );
      
      // Store user info for UI (non-sensitive data only)
      // The auth token is now in an httpOnly cookie
      setUserInfo(response.data.user);
      
      // Show success message
      setSignupSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (signupSuccess) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <Card className="signup-card">
            <CardContent className="success-content">
              <div className="success-icon">
                <CheckCircle2 size={64} className="text-green-500" />
              </div>
              <h2 className="success-title">Welcome to GoCalFit! 🎉</h2>
              <p className="success-message">
                Your account has been created successfully, <strong>{formData.firstName}</strong>!
              </p>
              <div className="success-email-notice">
                <p><strong>Check your email!</strong></p>
                <p>We've sent a welcome email to <strong>{formData.email}</strong></p>
                <p className="spam-notice">
                  📧 <strong>Don't see it?</strong> Check your spam/junk folder and move our email to your inbox. 
                  Add <strong>hello@gocalfit.com</strong> to your contacts!
                </p>
              </div>
              <Button 
                onClick={() => navigate('/waiver')} 
                className="signup-button"
                style={{ marginTop: '1.5rem' }}
              >
                Continue to Waiver
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <Card className="signup-card">
          <CardHeader>
            <CardTitle>Create Your Account</CardTitle>
            <CardDescription>Join GoCalFit and start your journey</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={fieldErrors.firstName ? 'input-error' : ''}
                    required
                  />
                  {fieldErrors.firstName && <span className="field-error">{fieldErrors.firstName}</span>}
                </div>
                
                <div className="form-group">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={fieldErrors.lastName ? 'input-error' : ''}
                    required
                  />
                  {fieldErrors.lastName && <span className="field-error">{fieldErrors.lastName}</span>}
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.email ? 'input-error' : ''}
                  required
                />
                {fieldErrors.email && <span className="field-error">{fieldErrors.email}</span>}
              </div>

              <div className="form-group">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(000) 000-0000"
                  value={formData.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.phone ? 'input-error' : ''}
                  required
                />
                {fieldErrors.phone && <span className="field-error">{fieldErrors.phone}</span>}
              </div>
              
              <div className="form-group">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="At least 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={fieldErrors.password ? 'input-error' : ''}
                  required
                />
                {fieldErrors.password && <span className="field-error">{fieldErrors.password}</span>}
              </div>

              <div className="form-group">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="Re-enter password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={fieldErrors.confirmPassword ? 'input-error' : ''}
                  required
                />
                {fieldErrors.confirmPassword && <span className="field-error">{fieldErrors.confirmPassword}</span>}
              </div>

              <div className="form-checkbox">
                <Checkbox
                  id="agreeToTerms"
                  name="agreeToTerms"
                  checked={formData.agreeToTerms}
                  onCheckedChange={(checked) => {
                    setFormData({ ...formData, agreeToTerms: checked });
                    if (fieldErrors.terms) setFieldErrors(prev => ({ ...prev, terms: '' }));
                  }}
                />
                <Label htmlFor="agreeToTerms" className="checkbox-label">
                  I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                </Label>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <Button 
                type="submit" 
                className="signup-button"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
          <CardFooter className="signup-footer">
            <p>Already have an account? <Link to="/login">Log in</Link></p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default SignupPage;
